import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import type {
  AgentRole,
  ProviderResolution,
  OrgAiConfig,
  ProviderName,
  ModelTier,
  OrgBillingMode,
} from '@chatsdet/types';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';

export type { AgentRole, ProviderResolution, OrgAiConfig, ProviderName, ModelTier };

export type JobStatus = 'queued' | 'running' | 'completed' | 'failed' | 'canceled';

export type OrganizationWithStats = {
  id: string;
  name: string;
  memberCount: number;
  createdAt: string;
  status: 'active' | 'inactive';
  trialEndsAt: string | null;
  billingMode: OrgBillingMode;
  isMember?: boolean;
  providerResolution?: ProviderResolution;
  /** The org's explicit AI override (null = inherits global defaults). */
  aiConfig?: OrgAiConfig | null;
};

export type JobDefinition = {
  jobType: string;
  maxAttempts: number | null;
  timeoutSeconds: number | null;
};

export type JobRecord = {
  id: string;
  jobType: string;
  status: JobStatus;
  orgId: string | null;
  groupId: string | null;
  parentJobId: string | null;
  payload: Record<string, unknown>;
  metadata: Record<string, unknown> | null;
  attempts: number;
  maxAttempts: number;
  runAt: string;
  startedAt: string | null;
  completedAt: string | null;
  failedAt: string | null;
  createdAt: string;
  updatedAt: string;
  lastError: string | null;
};

export type JobGroupRecord = {
  id: string;
  jobType: string;
  status: JobStatus;
  orgId: string | null;
  groupKey: string | null;
  queuedCount: number;
  runningCount: number;
  completedCount: number;
  failedCount: number;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
};

export type JobEventRecord = {
  id: string;
  jobId: string;
  groupId: string | null;
  eventType: string;
  data: Record<string, unknown>;
  createdAt: string;
};

export type FeatureFlagOrganization = {
  gateId: string;
  orgId: string;
  orgName: string;
  createdAt: string;
};

export type FeatureFlagUser = {
  gateId: string;
  userId: string;
  userEmail: string | null;
  createdAt: string;
};

export type FeatureFlag = {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  lastToggledAt: string;
  lifecycleStage: ConfigLifecycleStage;
  finalValue: boolean | null;
  /** Oven (baking) metadata. Present/relevant when lifecycleStage === 'baking'. */
  bakeMonitoringPlan: string | null;
  bakeStartedAt: string | null;
  bakeReviewIntervalDays: number | null;
  bakeMaxWindowDays: number | null;
  isStale: boolean;
  productCardId: string | null;
  productTitle: string | null;
  organizations: FeatureFlagOrganization[];
  users: FeatureFlagUser[];
};

/** Flag state for one org: enabled, or disabled-but-was-enabled history. */
export type OrgFeatureFlagState = {
  flagId: string;
  name: string;
  description: string | null;
  enabled: boolean;
  disabledAt: string | null;
  lifecycleStage: ConfigLifecycleStage;
  createdAt: string;
};

export type ProductCardFlag = {
  id: string;
  name: string;
  lifecycleStage: ConfigLifecycleStage;
  activeOrgGateCount: number;
  /** Rollout / A-B treatment-assignment percentage (0-100), or null if not set. */
  rolloutPercentage: number | null;
  /** Markdown bake/monitoring plan, when the flag has one stored. */
  bakeMonitoringPlan: string | null;
  bakeStartedAt: string | null;
  bakeReviewIntervalDays: number | null;
  bakeMaxWindowDays: number | null;
};

export type ConfigLifecycleStage =
  | 'active'
  | 'baking'
  | 'internal'
  | 'deprecated'
  | 'ready_for_cleanup';

export type Knob = {
  key: string;
  description: string | null;
  valueType: 'boolean' | 'string' | 'number' | 'json';
  value: unknown;
  lifecycleStage: ConfigLifecycleStage;
  finalValue: unknown;
  updatedAt: string | null;
  updatedBy: string | null;
  createdAt: string | null;
  isStale: boolean;
};

export const statusVariants: Record<
  JobStatus,
  'secondary' | 'warning' | 'success' | 'destructive' | 'outline'
> = {
  queued: 'secondary',
  running: 'warning',
  completed: 'success',
  failed: 'destructive',
  canceled: 'outline',
};

export const statusLabels: Record<JobStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  canceled: 'Canceled',
};

export const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
};

export const formatRelative = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const deltaMs = Date.now() - date.getTime();
  const deltaSeconds = Math.round(deltaMs / 1000);
  if (deltaSeconds < 60) return `${deltaSeconds}s ago`;
  const deltaMinutes = Math.round(deltaSeconds / 60);
  if (deltaMinutes < 60) return `${deltaMinutes}m ago`;
  const deltaHours = Math.round(deltaMinutes / 60);
  if (deltaHours < 24) return `${deltaHours}h ago`;
  const deltaDays = Math.round(deltaHours / 24);
  return `${deltaDays}d ago`;
};

export const formatBytes = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  if (value < 1024) return `${value} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const precision = unitIndex === 0 ? 0 : 1;
  return `${size.toFixed(precision)} ${units[unitIndex]}`;
};

export const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export type OrganizationsQueryParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

export type OrganizationsQueryOptions = {
  enabled?: boolean;
};

export type PaginatedOrganizationsResponse = {
  ok: boolean;
  items: OrganizationWithStats[];
  /** Alias for items — backward compat for callers that use .organizations */
  organizations: OrganizationWithStats[];
  pagination: { page: number; pageSize: number; totalItems: number; totalPages: number };
};

export function useOrganizationsQuery(
  params: OrganizationsQueryParams = {},
  options: OrganizationsQueryOptions = {}
) {
  return useQuery({
    queryKey: ['superadmin-organizations', params],
    enabled: options.enabled ?? true,
    queryFn: async (): Promise<PaginatedOrganizationsResponse> => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', params.page.toString());
      if (params.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params.search) searchParams.set('search', params.search);
      if (params.sortBy) searchParams.set('sortBy', params.sortBy);
      if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);
      const qs = searchParams.toString();
      const res = await fetch(`${API_URL}/superadmin/organizations${qs ? `?${qs}` : ''}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        throw await toApiError(res, 'Failed to fetch organizations');
      }
      const data = await res.json();
      return { ...data, organizations: data.items };
    },
  });
}

export type OrgMember = {
  userId: string;
  displayName: string | null;
  primaryEmail: string;
  role: 'admin' | 'member';
};

export function useOrgMembersQuery(orgId: string | null) {
  return useQuery({
    queryKey: ['superadmin-org-members', orgId],
    queryFn: async (): Promise<{ ok: boolean; members: OrgMember[] }> => {
      const res = await fetch(`${API_URL}/superadmin/organizations/${orgId}/members`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch org members');
      return res.json();
    },
    enabled: !!orgId,
  });
}

export function useDeleteOrganizationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orgId: string) => {
      const res = await fetch(`${API_URL}/superadmin/organizations/${orgId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error ?? 'Failed to delete organization');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-organizations'] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['superadmin-stats'] }).catch(() => {});
    },
  });
}

export function useBulkJoinOrganizationsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orgIds: string[]) => {
      const results = await Promise.allSettled(
        orgIds.map((id) =>
          fetch(`${API_URL}/superadmin/organizations/${id}/join`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
          })
        )
      );
      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length > 0) {
        throw new Error(`Failed to join ${failures.length} organization(s)`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-organizations'] }).catch(() => {});
    },
  });
}

export type SuperadminStats = {
  totalOrganizations: number;
  totalUsers: number;
};

export function useSuperadminStatsQuery() {
  return useQuery({
    queryKey: ['superadmin-stats'],
    queryFn: async (): Promise<{ ok: boolean; stats: SuperadminStats }> => {
      const res = await fetch(`${API_URL}/superadmin/stats`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch superadmin stats');
      return res.json();
    },
  });
}

export function useJobDefinitionsQuery() {
  return useQuery({
    queryKey: ['superadmin-job-definitions'],
    queryFn: async (): Promise<{ definitions: JobDefinition[] }> => {
      const res = await fetch(`${API_URL}/superadmin/job-definitions`, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
        throw new Error('Failed to load job definitions');
      }
      return res.json();
    },
  });
}

export function useJobsQuery(jobTypeFilter: string, statusFilter: 'all' | JobStatus) {
  return useQuery({
    queryKey: ['superadmin-jobs', jobTypeFilter, statusFilter],
    queryFn: async (): Promise<{ jobs: JobRecord[] }> => {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (jobTypeFilter !== 'all') params.set('jobType', jobTypeFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const res = await fetch(`${API_URL}/superadmin/jobs?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
        throw new Error('Failed to load jobs');
      }
      const json = await res.json();
      return { jobs: json.jobs as JobRecord[] };
    },
    refetchInterval: 10_000,
  });
}

export function useJobGroupsQuery() {
  return useQuery({
    queryKey: ['superadmin-job-groups'],
    queryFn: async (): Promise<{ groups: JobGroupRecord[] }> => {
      const res = await fetch(`${API_URL}/superadmin/job-groups?limit=20`, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
        throw new Error('Failed to load job groups');
      }
      const json = await res.json();
      return { groups: json.groups as JobGroupRecord[] };
    },
    refetchInterval: 15_000,
  });
}

export function useJobEventsQuery(jobId: string | null) {
  return useQuery({
    queryKey: ['superadmin-job-events', jobId],
    queryFn: async (): Promise<{ events: JobEventRecord[] }> => {
      if (!jobId) return { events: [] };
      const res = await fetch(`${API_URL}/superadmin/jobs/${jobId}/events?limit=100`, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
        throw new Error('Failed to load job events');
      }
      const json = await res.json();
      return { events: json.events as JobEventRecord[] };
    },
    enabled: Boolean(jobId),
    refetchInterval: 5000,
  });
}

export function useRunJobMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      jobType: string;
      payload: Record<string, unknown>;
      orgId?: string;
      metadata?: Record<string, unknown>;
      broker?: { entityId: string; orgId: string };
    }) => {
      const res = await fetch(`${API_URL}/superadmin/jobs/run`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          data?.error ?? (res.status === 403 ? 'Access denied' : 'Failed to run job')
        );
      }
      return res.json() as Promise<{ job: JobRecord; brokerJobId: string | null }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-jobs'] }).catch(() => {});
    },
  });
}

export function useJoinOrganizationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orgId: string) => {
      const res = await fetch(`${API_URL}/superadmin/organizations/${orgId}/join`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          data?.error ?? (res.status === 403 ? 'Access denied' : 'Failed to join organization')
        );
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-organizations'] }).catch(() => {});
    },
  });
}

export function useLeaveOrganizationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orgId: string) => {
      const res = await fetch(`${API_URL}/superadmin/organizations/${orgId}/leave`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          data?.error ?? (res.status === 403 ? 'Access denied' : 'Failed to leave organization')
        );
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-organizations'] }).catch(() => {});
    },
  });
}

/**
 * Update an org's AI provider + model-tier config. Send `''`/`null` for any
 * field to clear it back to the global default. Invalidates the orgs list so
 * the resolved provider/model columns refresh.
 */
export function useUpdateOrgAiConfigMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      orgId: string;
      provider: ProviderName | '' | null;
      models: Partial<Record<ModelTier, string | null>>;
      agents: Partial<Record<AgentRole, { provider?: ProviderName | null; model?: string | null }>>;
    }) => {
      const res = await fetch(`${API_URL}/superadmin/organizations/${input.orgId}/ai-config`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          provider: input.provider,
          models: input.models,
          agents: input.agents,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error ?? 'Failed to update AI config');
      }
      return res.json() as Promise<{ ok: true; aiConfig: OrgAiConfig | null }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-organizations'] }).catch(() => {});
    },
  });
}

export function useFeatureFlagsQuery() {
  return useQuery({
    queryKey: ['superadmin-feature-flags'],
    queryFn: async (): Promise<{ flags: FeatureFlag[] }> => {
      const res = await fetch(`${API_URL}/superadmin/feature-flags`, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
        throw new Error('Failed to fetch feature flags');
      }
      return res.json();
    },
  });
}

/**
 * Flags enabled or previously enabled ("was enabled") for a specific org.
 * Backs the customer-card feature-flags panel. Superadmin only.
 */
export function useOrgFeatureFlagsQuery(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['superadmin-feature-flags-by-org', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<{ flags: OrgFeatureFlagState[] }> => {
      const res = await fetch(`${API_URL}/superadmin/feature-flags/by-organization/${orgId}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
        throw new Error('Failed to fetch org feature flags');
      }
      return res.json();
    },
  });
}

/** Flags linked to a product board item. Superadmin only. */
export function useProductCardFlagsQuery(cardId: string | null | undefined) {
  return useQuery({
    queryKey: ['superadmin-feature-flags-by-product', cardId],
    enabled: Boolean(cardId),
    queryFn: async (): Promise<{ flags: ProductCardFlag[] }> => {
      const res = await fetch(`${API_URL}/superadmin/feature-flags/by-product-card/${cardId}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
        throw new Error('Failed to fetch product feature flags');
      }
      return res.json();
    },
  });
}

export function useCreateFlagMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { name: string; description?: string }) => {
      const res = await fetch(`${API_URL}/superadmin/feature-flags`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          data?.error ?? (res.status === 403 ? 'Access denied' : 'Failed to create feature flag')
        );
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-feature-flags'] }).catch(() => {});
    },
  });
}

export function useEnableFlagForOrgMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { flagName: string; orgId: string }) => {
      const res = await fetch(
        `${API_URL}/superadmin/feature-flags/${encodeURIComponent(input.flagName)}/organizations/${input.orgId}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          // Plain enable; per-org rollout override/exclusion is set via the CLI.
          body: JSON.stringify({}),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          data?.error ?? (res.status === 403 ? 'Access denied' : 'Failed to enable feature flag')
        );
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-feature-flags'] }).catch(() => {});
      queryClient
        .invalidateQueries({ queryKey: ['superadmin-feature-flags-by-org'] })
        .catch(() => {});
    },
  });
}

export function useDisableFlagForOrgMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { flagName: string; orgId: string }) => {
      const res = await fetch(
        `${API_URL}/superadmin/feature-flags/${encodeURIComponent(input.flagName)}/organizations/${input.orgId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          data?.error ?? (res.status === 403 ? 'Access denied' : 'Failed to disable feature flag')
        );
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-feature-flags'] }).catch(() => {});
      queryClient
        .invalidateQueries({ queryKey: ['superadmin-feature-flags-by-org'] })
        .catch(() => {});
    },
  });
}

/**
 * Link or unlink a feature flag to/from a product board item
 * (`productCardId: null` clears the link). The server recomputes the card's
 * flag-driven stage, so the product board cache is invalidated too.
 */
export function useSetFlagProductCardMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { flagName: string; productCardId: string | null }) => {
      const res = await fetch(
        `${API_URL}/superadmin/feature-flags/${encodeURIComponent(input.flagName)}/product`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ productCardId: input.productCardId }),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          data?.error ?? (res.status === 403 ? 'Access denied' : 'Failed to update product link')
        );
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-feature-flags'] }).catch(() => {});
      queryClient
        .invalidateQueries({ queryKey: ['superadmin-feature-flags-by-product'] })
        .catch(() => {});
      // Linking/unlinking can move the card to its flag-derived stage.
      queryClient.invalidateQueries({ queryKey: ['product-items'] }).catch(() => {});
    },
  });
}

export function useSearchUsersQuery(query: string) {
  return useQuery({
    queryKey: ['superadmin-users-search', query],
    queryFn: async (): Promise<{
      users: Array<{ id: string; email: string; name: string | null }>;
    }> => {
      const res = await fetch(`${API_URL}/superadmin/users/search?q=${encodeURIComponent(query)}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to search users');
      return res.json();
    },
    enabled: query.length >= 2,
    placeholderData: (prev) => prev,
  });
}

export function useEnableFlagForUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { flagName: string; userId: string }) => {
      const res = await fetch(
        `${API_URL}/superadmin/feature-flags/${encodeURIComponent(input.flagName)}/users/${input.userId}`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          data?.error ??
            (res.status === 403 ? 'Access denied' : 'Failed to enable feature flag for user')
        );
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-feature-flags'] }).catch(() => {});
    },
  });
}

export function useDisableFlagForUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { flagName: string; userId: string }) => {
      const res = await fetch(
        `${API_URL}/superadmin/feature-flags/${encodeURIComponent(input.flagName)}/users/${input.userId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          data?.error ??
            (res.status === 403 ? 'Access denied' : 'Failed to disable feature flag for user')
        );
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-feature-flags'] }).catch(() => {});
    },
  });
}

export function useSetFeatureFlagLifecycleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      flagName: string;
      stage: ConfigLifecycleStage;
      finalValue?: boolean;
      monitoringPlan?: string;
      reviewIntervalDays?: number;
      maxWindowDays?: number;
    }) => {
      const res = await fetch(
        `${API_URL}/superadmin/feature-flags/${encodeURIComponent(input.flagName)}/lifecycle`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            stage: input.stage,
            finalValue: input.finalValue,
            monitoringPlan: input.monitoringPlan,
            reviewIntervalDays: input.reviewIntervalDays,
            maxWindowDays: input.maxWindowDays,
          }),
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.error ?? 'Failed to update feature flag lifecycle');
      }
      return data as {
        flag: {
          name: string;
          lifecycleStage: ConfigLifecycleStage;
          finalValue: boolean | null;
        };
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-feature-flags'] }).catch(() => {});
    },
  });
}

export function useKnobsQuery() {
  return useQuery({
    queryKey: ['superadmin-knobs'],
    queryFn: async (): Promise<{ knobs: Knob[] }> => {
      const res = await fetch(`${API_URL}/superadmin/knobs`, {
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          data?.error ??
            (res.status === 403 ? 'Access denied. Superadmin required.' : 'Failed to fetch knobs')
        );
      }
      return res.json();
    },
  });
}

export function useUpsertKnobMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      key: string;
      description?: string | null;
      valueType: Knob['valueType'];
      value: unknown;
    }) => {
      const res = await fetch(`${API_URL}/superadmin/knobs`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.error ?? 'Failed to save knob');
      }
      return data as { knob: Knob };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-knobs'] }).catch(() => {});
    },
  });
}

export function useToggleKnobMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (key: string) => {
      const res = await fetch(`${API_URL}/superadmin/knobs/${encodeURIComponent(key)}/toggle`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.error ?? 'Failed to toggle knob');
      }
      return data as { knob: Knob };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-knobs'] }).catch(() => {});
    },
  });
}

export function useSetKnobLifecycleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      key: string;
      stage: ConfigLifecycleStage;
      finalValue?: unknown;
    }) => {
      const res = await fetch(
        `${API_URL}/superadmin/knobs/${encodeURIComponent(input.key)}/lifecycle`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            stage: input.stage,
            finalValue: input.finalValue,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.error ?? 'Failed to update knob lifecycle');
      }
      return data as { knob: Knob };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-knobs'] }).catch(() => {});
    },
  });
}

type AbortJobResult = {
  ok: boolean;
  status: 'aborted' | 'already_terminal' | 'queued_for_abort' | 'not_found';
  jobStatus?: JobStatus;
  message?: string;
  error?: string;
};

export function useAbortJobMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { jobId: string; reason?: string }): Promise<AbortJobResult> => {
      const res = await fetch(`${API_URL}/superadmin/jobs/${input.jobId}/abort`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ reason: input.reason }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data?.error ?? (res.status === 403 ? 'Access denied' : 'Failed to abort job')
        );
      }
      return data as AbortJobResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-jobs'] }).catch(() => {});
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// CloudWatch Metrics Dashboard
// ─────────────────────────────────────────────────────────────────────────────

export type TimeRangePreset = '1h' | '3h' | '6h' | '12h' | '24h' | '7d' | 'custom';

export type MetricDataPoint = {
  timestamp: string;
  value: number;
};

export type MetricSeries = {
  id: string;
  label: string;
  dataPoints: MetricDataPoint[];
};

type MetricDefinition = {
  id: string;
  namespace: string;
  metricName: string;
  stat: 'Sum' | 'Average' | 'Maximum' | 'Minimum' | 'SampleCount';
  label: string;
  dimensionNames: string[];
};

type DimensionValue = {
  name: string;
  values: string[];
};

export type MetricQuery = {
  id: string;
  namespace: string;
  metricName: string;
  stat: 'Sum' | 'Average' | 'Maximum' | 'Minimum' | 'SampleCount';
  label: string;
  dimensions?: Record<string, string>;
};

type MetricsResponse = {
  ok: boolean;
  metrics: MetricSeries[];
  timeRange: { startTime: string; endTime: string };
  period: number;
  namespacePrefix: string;
  metricDefinitions: MetricDefinition[];
  error?: string;
};

type DimensionsResponse = {
  ok: boolean;
  namespace: string;
  metricName: string | null;
  dimensions: DimensionValue[];
  error?: string;
};

type DefinitionsResponse = {
  ok: boolean;
  definitions: MetricDefinition[];
  namespacePrefix: string;
  error?: string;
};

export type AlarmState = 'OK' | 'ALARM' | 'INSUFFICIENT_DATA';

export type AlarmInfo = {
  alarmName: string;
  alarmDescription: string | null;
  state: AlarmState;
  stateReason: string | null;
  stateUpdatedTimestamp: string | null;
  metricName: string | null;
  namespace: string | null;
  threshold: number | null;
  comparisonOperator: string | null;
  evaluationPeriods: number | null;
};

type AlarmsResponse = {
  ok: boolean;
  alarms: AlarmInfo[];
  cloudWatchConsoleUrl: string;
  error?: string;
};

export const alarmStateVariants: Record<AlarmState, 'success' | 'destructive' | 'secondary'> = {
  OK: 'success',
  ALARM: 'destructive',
  INSUFFICIENT_DATA: 'secondary',
};

export const alarmStateLabels: Record<AlarmState, string> = {
  OK: 'OK',
  ALARM: 'Alarm',
  INSUFFICIENT_DATA: 'No Data',
};

export const TIME_RANGE_PRESETS: { value: TimeRangePreset; label: string; hours: number }[] = [
  { value: '1h', label: 'Last 1 hour', hours: 1 },
  { value: '3h', label: 'Last 3 hours', hours: 3 },
  { value: '6h', label: 'Last 6 hours', hours: 6 },
  { value: '12h', label: 'Last 12 hours', hours: 12 },
  { value: '24h', label: 'Last 24 hours', hours: 24 },
  { value: '7d', label: 'Last 7 days', hours: 24 * 7 },
  { value: 'custom', label: 'Custom range', hours: 0 },
];

function getTimeRangeFromPreset(preset: TimeRangePreset): { start: Date; end: Date } {
  const end = new Date();
  const presetConfig = TIME_RANGE_PRESETS.find((p) => p.value === preset);
  const hours = presetConfig?.hours ?? 24;
  const start = new Date(end.getTime() - hours * 60 * 60 * 1000);
  return { start, end };
}

function getPeriodForTimeRange(hours: number): number {
  // Return period in seconds based on time range
  if (hours <= 1) return 60; // 1 minute
  if (hours <= 6) return 300; // 5 minutes
  if (hours <= 24) return 900; // 15 minutes
  if (hours <= 72) return 3600; // 1 hour
  return 3600; // 1 hour for longer ranges
}

/**
 * Fetch metric definitions including dimension requirements
 */
export function useMetricDefinitionsQuery(enabled = true) {
  return useQuery({
    queryKey: ['superadmin-metric-definitions'],
    queryFn: async (): Promise<DefinitionsResponse> => {
      const res = await fetch(`${API_URL}/superadmin/metrics/definitions`, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
        throw new Error('Failed to fetch metric definitions');
      }
      return res.json();
    },
    enabled,
    staleTime: 5 * 60 * 1000, // Definitions rarely change, cache for 5 minutes
  });
}

/**
 * Fetch available dimension values for a namespace
 */
export function useDimensionsQuery(namespace: string | null, metricName?: string, enabled = true) {
  return useQuery({
    queryKey: ['superadmin-dimensions', namespace, metricName],
    queryFn: async (): Promise<DimensionsResponse> => {
      const params = new URLSearchParams({ namespace: namespace! });
      if (metricName) params.set('metricName', metricName);
      const res = await fetch(`${API_URL}/superadmin/metrics/dimensions?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 403) {
          throw await toApiError(res, 'Failed to fetch dimensions');
        }
        return {
          ok: false,
          namespace: namespace!,
          metricName: metricName ?? null,
          dimensions: [],
          error: 'Failed to fetch dimensions',
        };
      }
      return res.json();
    },
    enabled: enabled && Boolean(namespace),
    staleTime: 60_000, // Cache dimensions for 1 minute
  });
}

/**
 * Fetch metrics data - supports both system metrics (no dimensions) and dimensioned queries
 */
export function useMetricsQuery(
  timeRange: { start: Date; end: Date },
  period: number,
  queries?: MetricQuery[],
  enabled = true
) {
  return useQuery({
    queryKey: [
      'superadmin-metrics',
      timeRange.start.toISOString(),
      timeRange.end.toISOString(),
      period,
      queries ? JSON.stringify(queries) : 'default',
    ],
    queryFn: async (): Promise<MetricsResponse> => {
      const params = new URLSearchParams({
        startTime: timeRange.start.toISOString(),
        endTime: timeRange.end.toISOString(),
        period: String(period),
      });
      if (queries && queries.length > 0) {
        params.set('queries', JSON.stringify(queries));
      }
      const res = await fetch(`${API_URL}/superadmin/metrics?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
        throw new Error('Failed to fetch metrics');
      }
      return res.json();
    },
    refetchInterval: 60_000, // Auto-refresh every minute
    enabled,
    staleTime: 30_000, // Consider data stale after 30 seconds
  });
}

export function useAlarmsQuery(enabled = true) {
  return useQuery({
    queryKey: ['superadmin-alarms'],
    queryFn: async (): Promise<AlarmsResponse> => {
      const res = await fetch(`${API_URL}/superadmin/alarms`, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
        throw new Error('Failed to fetch alarms');
      }
      return res.json();
    },
    refetchInterval: 30_000, // Auto-refresh every 30 seconds
    enabled,
    staleTime: 15_000, // Consider data stale after 15 seconds
  });
}

export function useTimeRangeState(initialPreset: TimeRangePreset = '24h') {
  const [preset, setPreset] = React.useState<TimeRangePreset>(initialPreset);
  const [customRange, setCustomRange] = React.useState<{ start: Date; end: Date } | null>(null);

  const timeRange = React.useMemo(() => {
    if (preset === 'custom' && customRange) {
      return customRange;
    }
    return getTimeRangeFromPreset(preset);
  }, [preset, customRange]);

  const hours = React.useMemo(() => {
    const diff = timeRange.end.getTime() - timeRange.start.getTime();
    return diff / (1000 * 60 * 60);
  }, [timeRange]);

  const period = React.useMemo(() => getPeriodForTimeRange(hours), [hours]);

  return {
    preset,
    setPreset,
    customRange,
    setCustomRange,
    timeRange,
    hours,
    period,
  };
}

// ============================================================================
// Workflow Management
// ============================================================================

export type SuperadminWorkflow = {
  id: string;
  name: string;
  description: string | null;
  flowType: 'setup' | 'test' | 'monitoring';
  status: string;
  orgId: string;
  orgName: string;
  createdAt: string;
  updatedAt: string;
};

export type WorkflowListParams = {
  orgId?: string;
  flowType?: 'setup' | 'test' | 'monitoring';
  search?: string;
  page?: number;
  pageSize?: number;
};

type WorkflowListResponse = {
  ok: boolean;
  data: SuperadminWorkflow[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
};

type WorkflowCopyInput = {
  sourceWorkflowId: string;
  targetOrgId: string;
  newWorkflowName?: string;
};

type WorkflowCopyResult = {
  ok: boolean;
  data?: {
    workflow: { id: string; name: string; orgId: string };
    copied: {
      property: { sourceId: string; targetId: string } | null;
      credentials: number;
      canonicalPages: number;
      canonicalActions: number;
      nodes: number;
      edges: number;
    };
  };
  error?: string;
};

export function useSuperadminWorkflowsQuery(params: WorkflowListParams = {}, enabled = true) {
  const searchParams = new URLSearchParams();
  if (params.orgId) searchParams.set('orgId', params.orgId);
  if (params.flowType) searchParams.set('flowType', params.flowType);
  if (params.search) searchParams.set('search', params.search);
  if (params.page) searchParams.set('page', params.page.toString());
  if (params.pageSize) searchParams.set('pageSize', params.pageSize.toString());

  const queryString = searchParams.toString();
  const url = `${API_URL}/superadmin/workflows${queryString ? `?${queryString}` : ''}`;

  return useQuery({
    queryKey: ['superadmin-workflows', params],
    queryFn: async (): Promise<WorkflowListResponse> => {
      const res = await fetch(url, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
        throw new Error('Failed to fetch workflows');
      }
      return res.json();
    },
    enabled,
  });
}

export function useCopyWorkflowMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: WorkflowCopyInput): Promise<WorkflowCopyResult> => {
      const res = await fetch(`${API_URL}/superadmin/workflows/copy`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to copy workflow');
      }

      return data;
    },
    onSuccess: () => {
      // Invalidate workflow queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['superadmin-workflows'] });
    },
  });
}

// ============================================================================
// Organization Quotas
// ============================================================================

export type OrgQuota = {
  id: string;
  maxProperties: number;
  createdAt: string;
  updatedAt: string;
};

export type OrgQuotaListItem = {
  org: { id: string; name: string };
  quota: OrgQuota | null;
  propertyCount: number;
};

type OrgQuotasResponse = {
  ok: boolean;
  data: OrgQuotaListItem[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
};

type UpdateOrgQuotaResponse = {
  ok: boolean;
  quota: OrgQuota & { orgId: string };
  error?: string;
};

export function useOrgQuotasQuery(
  params: { page?: number; pageSize?: number } = {},
  enabled = true
) {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', params.page.toString());
  if (params.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  const queryString = searchParams.toString();
  const url = `${API_URL}/superadmin/org-quotas${queryString ? `?${queryString}` : ''}`;

  return useQuery({
    queryKey: ['superadmin-org-quotas', params],
    queryFn: async (): Promise<OrgQuotasResponse> => {
      const res = await fetch(url, {
        credentials: 'include',
      });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
        throw new Error('Failed to fetch org quotas');
      }
      return res.json();
    },
    enabled,
  });
}

export async function updateOrgQuota(input: {
  orgId: string;
  maxProperties?: number;
}): Promise<UpdateOrgQuotaResponse> {
  const res = await fetch(`${API_URL}/superadmin/organizations/${input.orgId}/quotas`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ maxProperties: input.maxProperties }),
  });

  if (!res.ok) {
    throw await toApiError(res, 'Failed to update org quota');
  }

  const data = await res.json();

  if (data?.ok === false) {
    throw new Error(data.error || 'Failed to update org quota');
  }

  return data;
}

export function useUpdateOrgQuotaMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateOrgQuota,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-org-quotas'] });
    },
  });
}

// ============================================================================
// Run Quotas (monthly agentic-run quotas)
// ============================================================================

export type UsageQuotaListItem = {
  org: { id: string; name: string };
  quota: {
    monthStartDay: number;
    monthlyRunQuota: number | null;
    updatedAt: string | null;
  };
  period: { start: string; end: string };
  usage: { agenticRuns: number };
};

type UsageQuotasResponse = {
  ok: boolean;
  data: UsageQuotaListItem[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
};

export function useUsageQuotasQuery(
  params: { page?: number; pageSize?: number } = {},
  enabled = true
) {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', params.page.toString());
  if (params.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  const queryString = searchParams.toString();
  const url = `${API_URL}/superadmin/usage/quotas${queryString ? `?${queryString}` : ''}`;

  return useQuery({
    queryKey: ['superadmin-usage-quotas', params],
    queryFn: async (): Promise<UsageQuotasResponse> => {
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
        throw new Error('Failed to fetch usage quotas');
      }
      return res.json();
    },
    enabled,
  });
}

export function useUpdateUsageQuotaMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      orgId: string;
      monthStartDay?: number;
      monthlyRunQuota?: number | null;
    }) => {
      const body: Record<string, unknown> = {};
      if (input.monthStartDay !== undefined) body.monthStartDay = input.monthStartDay;
      if (input.monthlyRunQuota !== undefined) body.monthlyRunQuota = input.monthlyRunQuota;

      const res = await fetch(`${API_URL}/superadmin/usage/quota/${input.orgId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data?.ok === false) {
        throw new Error(data.error || 'Failed to update usage quota');
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-usage-quotas'] });
    },
  });
}

// ============================================================================
// Org status (active / inactive)
// ============================================================================

export function useToggleOrgStatusMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      orgId: string;
      status: 'active' | 'inactive';
      reason?: string | null;
    }) => {
      const res = await fetch(`${API_URL}/superadmin/organizations/${input.orgId}/status`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: input.status, reason: input.reason ?? null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.error ?? 'Failed to update organization status');
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-organizations'] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['superadmin-stats'] }).catch(() => {});
    },
  });
}

// ─── Hints ──────────────────────────────────────────

export type AdminHint = {
  id: string;
  location: string;
  title: string;
  content: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

export function useHintsAdminQuery() {
  return useQuery({
    queryKey: ['superadmin-hints'],
    queryFn: async (): Promise<{ hints: AdminHint[] }> => {
      const res = await fetch(`${API_URL}/superadmin/hints`, { credentials: 'include' });
      if (!res.ok) {
        if (res.status === 403) throw new Error('Access denied. Superadmin privileges required.');
        throw new Error('Failed to fetch hints');
      }
      return res.json();
    },
  });
}

export function useCreateHintMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      location: string;
      title: string;
      content: string;
      sortOrder?: number;
    }) => {
      const res = await fetch(`${API_URL}/superadmin/hints`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create hint');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-hints'] });
    },
  });
}

export function useUpdateHintMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      location?: string;
      title?: string;
      content?: string;
      sortOrder?: number;
    }) => {
      const { id, ...body } = input;
      const res = await fetch(`${API_URL}/superadmin/hints/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update hint');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-hints'] });
    },
  });
}

export function useDeleteHintMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/superadmin/hints/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to delete hint');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-hints'] });
    },
  });
}

// ─────────────── Builder Sessions ───────────────

export type BuilderSessionStatus =
  | 'pending'
  | 'exploring'
  | 'validating'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type SuperadminBuilderSession = {
  id: string;
  orgId: string;
  orgName: string;
  workflowId: string;
  workflowName: string;
  status: BuilderSessionStatus;
  goal: string;
  title: string;
  nodeCount: number;
  jobId: string | null;
  errorMessage: string | null;
  agentRunCount: number;
  createdAt: string;
  completedAt: string | null;
};

export type BuilderSessionAgentRun = {
  id: string;
  label: string;
  status: string;
  modelId: string | null;
  totalSteps: number;
  tokensPrompt: number | null;
  tokensCompletion: number | null;
  tokensTotal: number | null;
  finishReason: string | null;
  errorMessage: string | null;
  startedAt: string;
  completedAt: string | null;
};

type BuilderSessionValidationRun = {
  id: string;
  status: string;
  errorMessage: string | null;
  videoStorageKey: string | null;
  createdAt: string;
  finishedAt: string | null;
};

type SuperadminBuilderSessionDetail = {
  job: {
    id: string;
    orgId: string;
    orgName: string;
    workflowId: string;
    workflowName: string;
    status: BuilderSessionStatus;
    goal: string;
    title: string;
    nodeCount: number;
    jobId: string | null;
    errorMessage: string | null;
    videoStorageKey: string | null;
    videoTimelineJson: unknown | null;
    traceStorageKey: string | null;
    diagnosticsStorageKey: string | null;
    metadataJson: Record<string, unknown> | null;
    createdAt: string;
    completedAt: string | null;
  };
  agentRuns: BuilderSessionAgentRun[];
  validationRun: BuilderSessionValidationRun | null;
};

type BuilderSessionThought = {
  id: string;
  builderJobId: string;
  content: string;
  toolName: string | null;
  orderIndex: number;
  createdAt: string;
};

export function useSuperadminBuilderSessionsQuery(params: {
  orgId?: string;
  status?: string;
  page: number;
  pageSize: number;
}) {
  return useQuery({
    queryKey: [
      'superadmin-builder-sessions',
      params.orgId,
      params.status,
      params.page,
      params.pageSize,
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set('page', String(params.page));
      searchParams.set('pageSize', String(params.pageSize));
      if (params.orgId) searchParams.set('orgId', params.orgId);
      if (params.status) searchParams.set('status', params.status);

      const res = await fetch(`${API_URL}/superadmin/builder-sessions?${searchParams.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch builder sessions');
      return res.json() as Promise<{
        ok: boolean;
        data: SuperadminBuilderSession[];
        pagination: { page: number; pageSize: number; totalItems: number; totalPages: number };
      }>;
    },
  });
}

export function useSuperadminBuilderSessionDetailQuery(id: string | null) {
  return useQuery({
    queryKey: ['superadmin-builder-session-detail', id],
    enabled: Boolean(id),
    queryFn: async () => {
      if (!id) throw new Error('Missing id');
      const res = await fetch(`${API_URL}/superadmin/builder-sessions/${id}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch builder session detail');
      return res.json() as Promise<{ ok: boolean; data: SuperadminBuilderSessionDetail }>;
    },
  });
}

export function useSuperadminBuilderThoughtsQuery(id: string | null) {
  return useQuery({
    queryKey: ['superadmin-builder-thoughts', id],
    enabled: Boolean(id),
    queryFn: async () => {
      if (!id) throw new Error('Missing id');
      const res = await fetch(`${API_URL}/superadmin/builder-sessions/${id}/thoughts`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch builder thoughts');
      const data = (await res.json()) as { ok: boolean; data: BuilderSessionThought[] };
      return data.data;
    },
  });
}

export function useSuperadminBuilderVideoUrlQuery(id: string | null) {
  return useQuery({
    queryKey: ['superadmin-builder-video-url', id],
    enabled: Boolean(id),
    queryFn: async () => {
      if (!id) throw new Error('Missing id');
      const res = await fetch(`${API_URL}/superadmin/builder-sessions/${id}/video-url`, {
        credentials: 'include',
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { ok: boolean; url: string };
      return data.ok ? data.url : null;
    },
  });
}

export function useSuperadminBuilderTraceUrlQuery(id: string | null) {
  return useQuery({
    queryKey: ['superadmin-builder-trace-url', id],
    enabled: Boolean(id),
    queryFn: async () => {
      if (!id) throw new Error('Missing id');
      const res = await fetch(`${API_URL}/superadmin/builder-sessions/${id}/trace-url`, {
        credentials: 'include',
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { ok: boolean; viewerUrl: string };
      return data.ok ? data.viewerUrl : null;
    },
  });
}
